import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Drawer, Form, Input, Select, Button, Table, Alert, Space, Row, Col, Typography, Modal, Image, Spin, Tooltip, DatePicker, Upload, message, theme } from 'antd';
import { TABLE_PAGE_SIZE_OPTIONS, filterFormItemLayout, filterFormGridLayout } from '@utils/property';
import { prepareBCOSSUrl, getAdminInfo, formatCurrency, dateTimeFormat, numberProject } from '@utils/utils';
import { laborFeeCostBearers, laborFeePaymentMethods, CurrencyList, cardTypes } from '@utils/constants';
import { fetchLaborFeePaymentManageList, fetchLaborFeeList, confirmPaymentOrAddCert, uploadFileAPI, remarkPaymentException, fetchLaborFeeBatchStatisticalAmount, printPaymentDetail } from '@services/Finance/FinanceLaborFee.service';
import { conferenceOnceApi } from '@services/Conference';
import { localStorageGet } from '@core/cookie';
import { post } from '@core/request';
import dayjs from 'dayjs';
import config from '@/config';
import ExpertInfoCard from '@/components/common/ExpertInfoCard';
import FinanceApprovalDetailModal from './FinanceApprovalDetailModal';
import PaymentApplicationDetailDrawer from './PaymentApplicationDetailDrawer';
import './LaborFeePaymentDrawer.less';

const { Text } = Typography;
const { confirm } = Modal;

const getBatchStatusClassName = batch => {
    if (batch.nodeId === 10 && batch.approvalStatus === 3) {
        return 'batch-status--success';
    }
    const statusMap = {
        0: 'batch-status--default',
        1: 'batch-status--warning',
        2: 'batch-status--success',
        3: 'batch-status--error',
    };
    return statusMap[batch.approvalStatus] || 'batch-status--default';
};

export default function LaborFeePaymentDrawer({ open, onClose, projectId, projectCode = 'YHY', selectedBatchNo: propSelectedBatchNo }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { token } = theme.useToken();

    const [filterForm] = Form.useForm();
    const [confirmPaymentForm] = Form.useForm();
    const [addCertForm] = Form.useForm();
    const [addPaymentExceptionRemarkForm] = Form.useForm();

    // 批次相关状态
    const [batchList, setBatchList] = useState([]);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [selectedBatchNo, setSelectedBatchNo] = useState(null);
    const [batchListLoading, setBatchListLoading] = useState(false);

    // 表格数据
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [filterValues, setFilterValues] = useState({});

    // 会议信息
    const [conference, setConference] = useState(null);

    // 参会凭证预览
    const [previewImage, setPreviewImage] = useState(null);

    // 付款申请明细弹窗状态
    const [paymentDetailVisible, setPaymentDetailVisible] = useState(false);

    // 审批详情弹窗
    const [approvalDetailVisible, setApprovalDetailVisible] = useState(false);

    // 统计信息
    const [batchStatistics, setBatchStatistics] = useState({});

    // 确认支付
    const [visibleConfirmPayment, setVisibleConfirmPayment] = useState(false);
    // 付款异常备注
    const [visiblePaymentException, setVisiblePaymentException] = useState(false);
    // 上传付款凭证
    const [visiblePaymentCert, setVisiblePaymentCert] = useState(false);

    // 表格选中（待）付款记录ID列表
    const [selectedPaymentRecordIdList, setSelectedPaymentRecordIdList] = useState([]);
    // 表格中已付款记录ID列表
    const [payedRecordIdList, setPayedRecordIdList] = useState([]);

    // 上传参会凭证
    const [currentPaymentId, setCurrentPaymentId] = useState(0);
    const [currentPaymentCert, setCurrentPaymentCert] = useState('');
    // 付款记录
    const [currentPayment, setCurrentPayment] = useState(null);

    // 是否拥有财务劳务费付款人员权限
    const isLaborFeePayment = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList && roleList.length) {
            return roleList.some(item => item.name === '财务劳务费付款人员');
        }
        return false;
    }, []);

    // 是否拥有财务劳务费管理员权限
    // const isLaborFeeManager = useMemo(() => {
    //     const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
    //     if (roleList && roleList.length) {
    //         return roleList.some(item => item.name === '财务劳务费管理员');
    //     }
    //     return false;
    // }, []);

    // 获取专家证件类型
    const getIdCardType = cardType => {
        const type = parseInt(cardType, 10);
        const target = cardTypes.find(item => item.code === type);
        return target?.name || null;
    };

    // 获取币种名称
    const getCurrencyName = currencyCode => {
        const currency = CurrencyList.find(c => c.currency === currencyCode);
        return currency ? currency.name : currencyCode;
    };

    // 获取总额
    const getTotalAmount = (laborFeeSum, individualTaxSum) => {
        if (laborFeeSum !== null && individualTaxSum !== null) {
            return (Number(laborFeeSum) + Number(individualTaxSum)).toFixed(2);
        }
        return '0.00';
    };

    // 获取付款批次列表
    const fetchBatchList = async sign => {
        if (!projectId) return;
        try {
            setBatchListLoading(true);
            const res = await fetchLaborFeePaymentManageList({
                projectCode: projectCode,
                projectId: projectId,
                requestFrom: 2,
            });
            if (res.status_code === 200 && res.data && Array.isArray(res.data)) {
                // 过滤只显示nodeId为10的批次
                const filteredBatches = res.data
                    .filter(item => item.nodeId === 10)
                    .map(item => ({
                        ...item,
                        id: item.batchNo,
                    }));
                setBatchList(filteredBatches);

                if (!!sign) {
                    // 审批弹窗回来 若审批列表数据为空，则关闭弹窗
                    if (filteredBatches.length === 0) {
                        onClose();
                        return;
                    }
                }

                // 优先检查是否有由于 prop 传递的 propSelectedBatchNo，不然默认选中第一个批次
                if (filteredBatches.length > 0) {
                    let targetBatch = null;
                    if (propSelectedBatchNo) {
                        // 查找匹配的批次
                        targetBatch = filteredBatches.find(b => b.batchNo === propSelectedBatchNo);
                    }
                    // 如果没有找到匹配的批次或没有 propSelectedBatchNo，或 selectedBatchId 已经设置，默认选中第一个
                    if (!targetBatch && !selectedBatchId) {
                        targetBatch = filteredBatches[0];
                    } else if (targetBatch) {
                        // 会覆盖 selectedBatchId
                    } else {
                        // selectedBatchId 已设置，不需要更改
                        return;
                    }

                    if (targetBatch) {
                        setSelectedBatchId(targetBatch.id);
                        setSelectedBatchNo(targetBatch.batchNo);
                    }
                }
            }
        } catch (error) {
            console.error('获取付款批次列表失败:', error);
        } finally {
            setBatchListLoading(false);
        }
    };

    // 选择批次
    const handleSelectBatch = batchId => {
        const batch = batchList.find(b => b.id === batchId);
        if (batch) {
            setSelectedBatchId(batchId);
            setSelectedBatchNo(batch.batchNo);
            setPage(1);
            setFilterValues({});
            filterForm.resetFields();
            setSelectedPaymentRecordIdList([]);
        }
    };

    // 获取会议信息
    const getConferenceInfo = async () => {
        if (!projectId) return;
        try {
            const res = await conferenceOnceApi({ id: projectId });
            if (res.status_code === 200 && res.data) {
                setConference(res.data);
            }
        } catch (error) {
            console.error('获取会议信息失败:', error);
        }
    };

    // 获取批次劳务费统计总额
    const getBatchStatistics = async () => {
        if (!projectId || !selectedBatchNo) return;

        try {
            const params = {
                projectId,
                batchNo: selectedBatchNo,
                ...filterValues,
            };
            const res = await fetchLaborFeeBatchStatisticalAmount(params);
            if (res && res.status_code === 200) {
                setBatchStatistics(res.data || {});
            }
        } catch (error) {
            console.error('获取批次统计总额失败:', error);
        }
    };

    // 获取劳务费明细列表
    const getLaborFeeList = async () => {
        if (!projectId || !selectedBatchNo) return;

        try {
            setLoading(true);
            const params = {
                page,
                pageSize,
                projectId,
                batchNo: selectedBatchNo,
                maskFlag: 1, // 默认脱敏，1-不脱敏
                ...filterValues,
            };

            const res = await fetchLaborFeeList(params);
            if (res && res.status_code === 200) {
                const laborFeeList = res.data?.list || [];
                const payedIdList = [];

                // 列表数据拿到后，根据批次号与每条数据中的laborFeePayRecordDtoList进行匹配，匹配上的laborFeePayRecordDtoList其中的一项的ID，作为该条数据的paymentId
                laborFeeList.forEach(item => {
                    const matchedRecord = item.laborFeePayRecordDtoList.find(record => record.batchNo === selectedBatchNo);
                    if (matchedRecord) {
                        item.paymentId = matchedRecord.id;
                        item.paymentCert = matchedRecord.paymentCert || '';
                        item.payedAt = matchedRecord.payedAt || '';
                        item.notes = matchedRecord.notes || '';
                        item.notesAt = matchedRecord.notesAt || '';

                        if (!!matchedRecord.payedAt) {
                            payedIdList.push(matchedRecord.id);
                        }
                    }
                });

                setPayedRecordIdList(payedIdList);
                setData(laborFeeList);
                setTotal(res.data?.totalCount || 0);
            }
        } catch (error) {
            console.error('获取劳务费列表失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 表单提交
    const onFormFinish = values => {
        setFilterValues(values);
        setPage(1);
    };

    // 重置表单
    const onReset = () => {
        filterForm.resetFields();
        setFilterValues({});
        setPage(1);
    };

    // 页码改变
    const onChangePage = (newPage, newPageSize) => {
        setPage(newPage);
        setPageSize(newPageSize);
    };

    // 打开确认付款弹窗
    const onViewConfirmPaymentModal = paymentId => {
        if (paymentId) {
            setSelectedPaymentRecordIdList([paymentId]);
        }
        setVisibleConfirmPayment(true);
    };

    // 确认付款
    const onConfirmPayment = () => {
        confirmPaymentForm.validateFields().then(values => {
            confirm({
                title: '再次确认支付',
                content: '提交后不允许修改，是否确认提交？',
                centered: true,
                onOk: async () => {
                    const data = {
                        ...values,
                        type: 1, // 1.确认付款，2.上传凭证
                        paymentIds: selectedPaymentRecordIdList,
                    };
                    data.payedAt = dayjs(data.payedAt).isValid() ? dateTimeFormat(data.payedAt, 'YYYY-MM-DD') : '';

                    const res = await confirmPaymentOrAddCert(data);
                    if (res.status_code === 200) {
                        getLaborFeeList();
                        message.success('确认支付成功');
                        setVisibleConfirmPayment(false);
                        confirmPaymentForm.resetFields();
                        setSelectedPaymentRecordIdList([]);
                    }
                },
                onCancel: () => {
                    setVisibleConfirmPayment(false);
                },
            });
        });
    };

    // 打开上传付款凭证弹窗
    const onViewAddCertModal = record => {
        setCurrentPayment(record);
        setCurrentPaymentId(record.paymentId);
        setCurrentPaymentCert(record.paymentCert || '');
        setVisiblePaymentCert(true);
    };

    // 打开付款异常备注弹窗
    const onViewAddPaymentExceptionRemark = async record => {
        setCurrentPaymentId(record.paymentId);
        setVisiblePaymentException(true);
    };

    // 添加付款异常备注
    const onAddPaymentExceptionRemark = () => {
        addPaymentExceptionRemarkForm.validateFields().then(async values => {
            const res = await remarkPaymentException({
                id: currentPaymentId,
                ...values,
            });
            if (res.status_code === 200) {
                message.success('添加付款异常备注成功');
                setCurrentPaymentId(0);
                addPaymentExceptionRemarkForm.resetFields();
                setVisiblePaymentException(false);
                getLaborFeeList();
            }
        });
    };

    // 检查上传文件
    const checkUploadFile = (file, size) => {
        const rightFileTypes = file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png';
        if (!rightFileTypes) {
            message.error('请上传 JPG/PNG 格式图片');
        }

        const rightFileSize = file.size / 1024 / 1024 <= size;
        if (!rightFileSize) {
            message.error('请上传' + size + 'MB以内的附件');
        }

        return rightFileTypes && rightFileSize;
    };

    // 上传付款凭证
    const paymentCertFile = {
        name: 'paymentCertFile',
        onChange(info) {
            if (info.file.status === 'uploading') {
                info.file.status = 'done';
            }
        },
        customRequest: info => {
            const formData = new FormData();
            formData.append('file', info.file);
            formData.append('path', dayjs().format('YYYYMM'));

            uploadFileAPI(formData).then(async res => {
                if (!res) {
                    message.error('付款凭证上传失败');
                    return false;
                }

                if (res.status_code === 200) {
                    setCurrentPaymentCert(res.data);
                    const data = {
                        paymentIds: [currentPaymentId],
                        type: 2,
                        paymentCert: res.data,
                    };
                    const certRes = await confirmPaymentOrAddCert(data);
                    if (certRes.status_code === 200) {
                        message.success('付款凭证上传成功');
                        getLaborFeeList();
                    }
                }
            });
        },
    };

    // 导出付款申请明细
    const onExportPaymentList = async () => {
        // message.success('请稍后，正在导出');
        // const res = await exportLaborFeePaymentList({ batchNo: selectedBatchNo });
        // let blob = new Blob([res], { type: 'application/octet-stream' });

        // if ('download' in document.createElement('a')) {
        //     const link = document.createElement('a');
        //     link.style.display = 'none';
        //     link.download = `劳务费请款批次_${selectedBatchNo}_付款明细表.xlsx`;
        //     link.href = URL.createObjectURL(blob);
        //     document.body.appendChild(link);
        //     link.click();
        //     document.body.removeChild(link);
        //     link.remove();
        // } else {
        //     navigator.msSaveBlob(blob);
        // }

        window.location.href = `${config.API_PREFIX}/user/laborAudit/pay-batch-infos-download?batchNo=${selectedBatchNo}`;
    };

    // 下载付款申请明细
    const handlePrintPaymentDetail = () => {
        setPaymentDetailVisible(true);
    };

    // 确认付款明细已打印
    const confirmPrintPaymentDetail = async () => {
        confirm({
            title: '确认提示',
            content: '确认该批次付款明细已全部打印',
            centered: true,
            onOk: async () => {
                try {
                    // 使用现有的API服务函数来更新打印状态
                    // 如果后端没有专门的API，我们可能需要使用通用的更新方法
                    const res = await printPaymentDetail({
                        batchNo: selectedBatchNo,
                    });

                    if (res.status_code === 200) {
                        message.success('已确认');
                        // 刷新统计数据以更新打印状态
                        getBatchStatistics();
                    } else {
                        throw new Error(res.message || '更新打印状态失败');
                    }
                } catch (error) {
                    console.error('确认打印失败:', error);
                    message.error(error.message || '确认打印失败，请重试');
                }
            },
            onCancel: () => {},
        });
    };

    // 表格行选择
    const rowSelection = {
        selectedRowKeys: selectedPaymentRecordIdList, // 控制哪些行被选中
        onChange: (selectedRowKeys, selectedRows) => {
            console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
            if (!selectedRows) {
                setSelectedPaymentRecordIdList([]);
            } else {
                // 过滤掉已付款的记录，确保只有未付款的记录被选中
                const arr = [];
                !!selectedRows.length
                    && selectedRows.forEach(r => {
                        // 只有当记录未付款时才添加到选中列表
                        if (!r.payedAt) {
                            arr.push(r.paymentId);
                        }
                    });
                setSelectedPaymentRecordIdList(arr);
                console.log(arr);
            }
        },
        getCheckboxProps: record => ({
            disabled: payedRecordIdList.indexOf(record.paymentId) > -1,
        }),
    };

    // 表格列定义
    const columns = [
        {
            title: '序号',
            width: 60,
            fixed: 'left',
            render: (_, __, index) => (page - 1) * pageSize + index + 1,
        },
        {
            title: '基本信息',
            dataIndex: 'memUsername',
            width: 200,
            fixed: 'left',
            render: (_, record) => <ExpertInfoCard expert={record} />,
        },
        {
            title: '本次申请劳务费',
            dataIndex: 'currentBatchLaborFee',
            width: 250,
            render: (_, record) => {
                // 从付款申请记录中找到当前批次的数据
                const payRecords = record.laborFeePayRecordDtoList || [];
                const currentBatchRecord = payRecords.find(payRecord => payRecord.batchNo === selectedBatchNo);

                if (!currentBatchRecord) {
                    return <Text type="secondary">暂无</Text>;
                }

                const isForeignCurrency = currentBatchRecord.currency && currentBatchRecord.currency !== 'CNY';

                return (
                    <>
                        <div>
                            <Text>付款方式：</Text>
                            {currentBatchRecord.paymentMethod ? laborFeePaymentMethods.find(m => m.value === currentBatchRecord.paymentMethod)?.label : <Text type="secondary">&ndash;</Text>}
                        </div>
                        <div>
                            <Text>支付主体：</Text>
                            {currentBatchRecord.costBearer ? laborFeeCostBearers.find(b => b.value === currentBatchRecord.costBearer)?.label : <Text type="secondary">&ndash;</Text>}
                        </div>
                        <div>
                            <Text>支付币种：</Text>
                            {currentBatchRecord.currency ? getCurrencyName(currentBatchRecord.currency) : <Text type="secondary">&ndash;</Text>}
                        </div>
                        {isForeignCurrency ? (
                            <>
                                <div>
                                    <Text>外币实发额({getCurrencyName(currentBatchRecord.currency)})：</Text>
                                    {currentBatchRecord.foreignExchangeAmount ? formatCurrency(currentBatchRecord.foreignExchangeAmount) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>折合人民币实发劳务费(元)：</Text>
                                    {currentBatchRecord.laborFeeAmount ? formatCurrency(currentBatchRecord.laborFeeAmount) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>折合人民币个人所得税(元)：</Text>
                                    {currentBatchRecord.individualTaxAmount ? formatCurrency(currentBatchRecord.individualTaxAmount) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>折合人民币含税劳务费(元)：</Text>
                                    {currentBatchRecord.laborFeeAmount && currentBatchRecord.individualTaxAmount ? formatCurrency(parseFloat(currentBatchRecord.laborFeeAmount) + parseFloat(currentBatchRecord.individualTaxAmount)) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>专家参会照片盖章版：</Text>
                                    {currentBatchRecord.expertParticipationPic ? (
                                        <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => setPreviewImage(currentBatchRecord.expertParticipationPic)}>
                                            查看
                                        </Button>
                                    ) : (
                                        <Text type="warning">未上传</Text>
                                    )}
                                </div>
                                <div>
                                    <Text>专家劳务确认函盖章版：</Text>
                                    {currentBatchRecord.expertLaborFeeConfirmPic ? (
                                        <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => setPreviewImage(currentBatchRecord.expertLaborFeeConfirmPic)}>
                                            查看
                                        </Button>
                                    ) : (
                                        <Text type="warning">未上传</Text>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <Text>实发劳务费(元)：</Text>
                                    {currentBatchRecord.laborFeeAmount ? formatCurrency(currentBatchRecord.laborFeeAmount) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>个人所得税(元)：</Text>
                                    {currentBatchRecord.individualTaxAmount ? formatCurrency(currentBatchRecord.individualTaxAmount) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>含税劳务费(元)：</Text>
                                    {currentBatchRecord.laborFeeAmount && currentBatchRecord.individualTaxAmount ? formatCurrency(parseFloat(currentBatchRecord.laborFeeAmount) + parseFloat(currentBatchRecord.individualTaxAmount)) : <Text type="secondary">&ndash;</Text>}
                                </div>
                            </>
                        )}
                    </>
                );
            },
        },
        {
            title: '收款信息',
            dataIndex: 'bankName',
            width: 250,
            render: (_, record) => (
                <>
                    {(record.idcardType === 1 || !record.idcardType) && (
                        <>
                            <span>身份证号码：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY年M月D日')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                    {record.idcardType > 1 && record.bankType !== 2 && (
                        <>
                            <span>证件类型：{getIdCardType(record.idcardType) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>证件号：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>国籍：{record.nationality || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY年M月D日')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生地：{record.birthPlace || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                    {record.idcardType > 1 && record.bankType === 2 && (
                        <>
                            <span>证件类型：{getIdCardType(record.idcardType) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>证件号：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>国籍：{record.nationality || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY年M月D日')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生地：{record.birthPlace || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人姓名(英文)：{record.payeeUsername || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人银行预留电话：{record.payeePhone || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人境外汇款住址(英文)：{record.payeeAddress || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户行名称(英文)：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>SWIFT/BIC Code：{record.swiftBicCode || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                </>
            ),
        },
        {
            title: '付款信息',
            dataIndex: 'paymentInfo',
            width: 200,
            render: (_, record) => (
                <>
                    <div>
                        <Text>付款日期：</Text>
                        {record.payedAt ? dayjs(record.payedAt).format('YYYY年M月D日') : <Text type="secondary">暂无</Text>}
                    </div>
                    <div>
                        <Text>付款凭证：</Text>
                        {record.paymentCert ? (
                            <Button type="link" size="small" style={{ paddingLeft: 0, paddingRight: 0 }} onClick={() => setPreviewImage(record.paymentCert)}>
                                查看
                            </Button>
                        ) : (
                            <Text type="secondary">暂无</Text>
                        )}
                    </div>
                    {isLaborFeePayment && (
                        <Button type="link" size="small" icon={<i className="bi bi-pencil"></i>} onClick={() => onViewAddCertModal(record)}>
                            {!record.paymentCert ? '上传付款凭证' : '修改付款凭证'}
                        </Button>
                    )}
                </>
            ),
        },
        {
            title: '付款异常备注',
            dataIndex: 'notes',
            width: 250,
            render: (_, record) => (
                <>
                    <div style={{ marginBottom: 8 }}>
                        {record.notes && (
                            <Alert
                                title={
                                    <div>
                                        <span>{record.notes}</span>
                                        <br />
                                        <Text type="secondary">{dayjs(record.notesAt).format('YYYY年M月D日 HH:mm:ss')}</Text>
                                    </div>
                                }
                                type="warning"
                            />
                        )}
                    </div>
                    {isLaborFeePayment && (
                        <Button type="link" size="small" icon={<i className="bi bi-pencil"></i>} onClick={() => onViewAddPaymentExceptionRemark(record)}>
                            填写备注
                        </Button>
                    )}
                </>
            ),
        },
    ];

    useEffect(() => {
        if (open && projectId) {
            fetchBatchList();
            getConferenceInfo();
        }
    }, [open, projectId]);

    useEffect(() => {
        if (open && projectId && selectedBatchNo) {
            getLaborFeeList();
            getBatchStatistics();
        }
    }, [open, projectId, selectedBatchNo, page, pageSize, filterValues]);

    // 关闭时重置状态
    const handleClose = () => {
        filterForm.resetFields();
        setFilterValues({});
        setPage(1);
        setPageSize(10);
        setData([]);
        setBatchList([]);
        setSelectedBatchId(null);
        setSelectedBatchNo(null);
        setSelectedPaymentRecordIdList([]);

        // 检查URL参数中是否有fromPage，如果有则返回到该页面
        const urlParams = new URLSearchParams(location.search || window.location.search);
        const fromPage = urlParams.get('fromPage');

        if (fromPage && fromPage !== window.location.pathname) {
            navigate(fromPage, { replace: true });
        } else {
            navigate({ pathname: location.pathname, search: '' }, { replace: true });
        }

        onClose();
    };

    // 获取当前选中批次的信息
    const currentBatch = useMemo(() => {
        return batchList.find(batch => batch.id === selectedBatchId);
    }, [batchList, selectedBatchId]);

    return (
        <Drawer title={<div className="labor-fee-payment-drawer__title">付款申请批次</div>} placement="bottom" size="100%" open={open} onClose={handleClose} className="labor-fee-payment-drawer" destroyOnHidden closable={{ placement: 'end' }}>
            <div className="drawer-content-container">
                {/* 左侧：付款批次列表 */}
                <div className="batch-list-panel">
                    <div className="batch-list-content">
                        {batchListLoading ? (
                            <div className="batch-loading">
                                <Spin />
                            </div>
                        ) : batchList.length > 0 ? (
                            <div className="batch-items">
                                {batchList.map(batch => {
                                    const formattedDate = batch.batchCreatedAt ? dayjs(batch.batchCreatedAt).format('YYYY年M月D日HH:mm:ss') : '—';

                                    const getNodeDisplay = () => {
                                        const nodeName = batch.nodeName || '';
                                        const approvalByName = batch.approvalByName || '';

                                        if (nodeName && approvalByName) {
                                            return `${nodeName}（${approvalByName}）`;
                                        } else if (nodeName) {
                                            return nodeName;
                                        }
                                        return '';
                                    };

                                    return (
                                        <div key={batch.id} className={`batch-item ${selectedBatchId === batch.id ? 'batch-item-selected' : ''}`} onClick={() => handleSelectBatch(batch.id)}>
                                            <div className="batch-date">付款批次：{formattedDate}</div>
                                            <div className={`batch-status ${getBatchStatusClassName(batch)}`}>
                                                <span>{getNodeDisplay() || '—'}</span>
                                                <span>{batch.approvalStatusName}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="empty-state">暂无付款批次</div>
                        )}
                    </div>
                </div>

                {/* 右侧：筛选和表格 */}
                <div className="detail-panel">
                    {selectedBatchNo ? (
                        <>
                            {/* 筛选表单 */}
                            <div className="filter-form-box">
                                <Form {...filterFormItemLayout} form={filterForm} onFinish={onFormFinish} autoComplete="off">
                                    <Row>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item name="memUsername" label="姓名">
                                                <Input placeholder="请输入专家姓名" allowClear />
                                            </Form.Item>
                                        </Col>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item name="laborPayStatus" label="付款状态">
                                                <Select placeholder="请选择" allowClear>
                                                    <Select.Option value={0}>待付款</Select.Option>
                                                    <Select.Option value={1}>已付款</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item name="laborPayStatus" label="付款凭证">
                                                <Select placeholder="请选择" allowClear>
                                                    <Select.Option value={0}>未上传</Select.Option>
                                                    <Select.Option value={1}>已上传</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item name="laborPayStatus" label="付款异常备注">
                                                <Select placeholder="请选择" allowClear>
                                                    <Select.Option value={0}>有备注</Select.Option>
                                                    <Select.Option value={1}>无备注</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item name="costBearer" label="支付主体">
                                                <Select placeholder="请选择" allowClear options={laborFeeCostBearers} fieldNames={{ label: 'label', value: 'value' }} />
                                            </Form.Item>
                                        </Col>
                                        <Col className="filter-form-buttons" {...filterFormGridLayout}>
                                            <Space>
                                                <Button type="default" onClick={onReset}>
                                                    重置
                                                </Button>
                                                <Button type="primary" icon={<i className="bi bi-search"></i>} htmlType="submit">
                                                    检索
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Form>
                            </div>

                            {/* 操作按钮 */}
                            <div className="action-buttons">
                                <Space>
                                    {isLaborFeePayment && currentBatch && currentBatch.approvalStatus !== 3 && (
                                        <Button type="primary" onClick={() => onViewConfirmPaymentModal(0)} disabled={selectedPaymentRecordIdList.length === 0}>
                                            确认付款
                                        </Button>
                                    )}
                                    {isLaborFeePayment && currentBatch && !batchStatistics?.printedAt && (
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                // 确认付款明细已打印的操作
                                                confirmPrintPaymentDetail();
                                            }}
                                        >
                                            确认付款明细已打印
                                        </Button>
                                    )}

                                    <Button type="primary" onClick={handlePrintPaymentDetail}>
                                        下载付款申请明细
                                    </Button>
                                    <Button type="default" onClick={() => setApprovalDetailVisible(true)}>
                                        审批详情
                                    </Button>
                                    {isLaborFeePayment && currentBatch && (
                                        <Tooltip title="导出付款申请明细">
                                            <Button
                                                icon={
                                                    <span className="anticon">
                                                        <i className="bi bi-cloud-download"></i>
                                                    </span>
                                                }
                                                onClick={onExportPaymentList}
                                            />
                                        </Tooltip>
                                    )}
                                </Space>
                            </div>

                            {/* 统计信息 */}
                            <div className="statistics-alert">
                                <Alert
                                    type="info"
                                    style={{ marginBottom: 16, fontSize: 12 }}
                                    title={
                                        <>
                                            <span className="statistics-alert__label">项目信息：</span>
                                            <span className="statistics-alert__text">
                                                {conference?.name || '-'}， 氚云项目编号：{conference?.h3yunNo || '-'}， 会议项目编号：{numberProject('YHY', conference?.id) || '-'}
                                            </span>
                                            <br />
                                            <span className="statistics-alert__label">本批次付款申请：</span>
                                            <span className="statistics-alert__text">
                                                付款申请人数：{batchStatistics?.realTotal || 0}人， 已付款人数：{batchStatistics?.expertPaySum || 0}人， 实发劳务费合计金额（元）：{formatCurrency(batchStatistics?.LaborFeeSum || 0)}， 个人所得税合计金额（元）：{formatCurrency(batchStatistics?.IndividualTaxSum || 0)}，
                                                含税劳务费合计金额（元）：{formatCurrency(getTotalAmount(batchStatistics?.LaborFeeSum, batchStatistics?.IndividualTaxSum))}
                                            </span>
                                            <br />
                                            <span className="statistics-alert__label">付款明细打印：</span>
                                            {batchStatistics?.printedAt ? (
                                                <Text type="success" strong style={{ fontSize: 12 }}>
                                                    已打印
                                                </Text>
                                            ) : (
                                                <Text type="warning" strong style={{ fontSize: 12 }}>
                                                    未打印
                                                </Text>
                                            )}
                                        </>
                                    }
                                />
                            </div>

                            {/* 表格 */}
                            <div className="table-box">
                                <Table
                                    rowKey="paymentId"
                                    columns={columns}
                                    dataSource={data}
                                    loading={loading}
                                    scroll={{ x: 800 }}
                                    pagination={{
                                        total,
                                        showTotal: () => `共${total}条`,
                                        current: page,
                                        pageSize: pageSize,
                                        pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                                        showQuickJumper: true,
                                        showSizeChanger: true,
                                        onChange: onChangePage,
                                    }}
                                    rowSelection={isLaborFeePayment ? { type: 'checkbox', ...rowSelection } : undefined}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="empty-state-large">请选择付款批次</div>
                    )}
                </div>
            </div>

            {/* 图片预览 Modal */}
            <Modal title="图片预览" open={!!previewImage} onCancel={() => setPreviewImage(null)} footer={null} centered width="auto" styles={{ body: { maxWidth: 800, padding: 30, textAlign: 'center', background: token.colorFillAlter } }} destroyOnHidden>
                {previewImage && <Image src={prepareBCOSSUrl(previewImage)} style={{ maxWidth: '100%', maxHeight: '100%' }} preview={false} alt="图片预览" />}
            </Modal>

            {/* 确认支付弹窗 */}
            <Modal title="确认支付" open={visibleConfirmPayment} onOk={onConfirmPayment} onCancel={() => setVisibleConfirmPayment(false)} centered={true} mask={{ closable: false }}>
                <Form layout="vertical" form={confirmPaymentForm}>
                    <Form.Item label="付款时间" name="payedAt" rules={[{ required: true, message: '付款时间不能为空' }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 上传付款凭证弹窗 */}
            <Modal title="上传付款凭证" open={visiblePaymentCert} onCancel={() => setVisiblePaymentCert(false)} centered={true} mask={{ closable: false }} footer={null}>
                <Form layout="vertical" form={addCertForm}>
                    {!!currentPayment?.memUsername && <Form.Item label="专家姓名">{currentPayment.memUsername}</Form.Item>}
                    <Form.Item label="付款凭证" className="required" extra="付款凭证支持 JPG/PNG 格式，大小不超过1MB">
                        {!!currentPaymentCert && (
                            <div style={{ marginBottom: 10 }}>
                                <Image width={200} src={prepareBCOSSUrl(currentPaymentCert)} alt="付款凭证" />
                            </div>
                        )}

                        <Upload beforeUpload={file => checkUploadFile(file, 1)} maxCount={1} {...paymentCertFile}>
                            <Button icon={<i className="bi bi-upload"></i>}>{!currentPaymentCert ? '上传图片' : '更新图片'}</Button>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 付款异常备注弹窗 */}
            <Modal title="付款异常备注" open={visiblePaymentException} onOk={onAddPaymentExceptionRemark} onCancel={() => setVisiblePaymentException(false)} centered={true} mask={{ closable: false }}>
                <Form layout="vertical" form={addPaymentExceptionRemarkForm}>
                    <Form.Item label="备注" name="refuseReason" rules={[{ required: true, message: '备注不能为空' }]}>
                        <Input.TextArea maxLength={100} rows={3} showCount />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 审批详情弹窗 */}
            {approvalDetailVisible && (
                <FinanceApprovalDetailModal
                    open={approvalDetailVisible}
                    onClose={() => setApprovalDetailVisible(false)}
                    paymentRecord={currentBatch}
                    projectId={projectId}
                    batchNo={selectedBatchNo}
                    statisticalAmount={batchStatistics}
                    onRefresh={() => {
                        setSelectedBatchNo(null);
                        fetchBatchList('onRefresh');
                        getLaborFeeList();
                        getBatchStatistics();
                    }}
                    currentUserPhone={getAdminInfo()?.adminPhone}
                    type="payment"
                />
            )}

            {/* 付款申请明细弹窗 */}
            {paymentDetailVisible && <PaymentApplicationDetailDrawer open={paymentDetailVisible} onClose={() => setPaymentDetailVisible(false)} batchNo={selectedBatchNo} projectId={projectId} />}
        </Drawer>
    );
}
